import React, { useRef, useState } from 'react';
import { Button, Form, message, Modal, Select, Tag as CustomTag } from 'antd';
import styled from 'styled-components';
import { SearchOutlined } from '@ant-design/icons';

import { useGetSearchResultsLazyQuery } from '../../../../../../../graphql/search.generated';
import { Domain, Entity, EntityType } from '../../../../../../../types.generated';
import { useBatchSetDomainMutation } from '../../../../../../../graphql/mutations.generated';
import { useEntityRegistry } from '../../../../../../useEntityRegistry';
import { useEnterKeyListener } from '../../../../../../shared/useEnterKeyListener';
import { DomainLabel } from '../../../../../../shared/DomainLabel';
import { handleBatchError } from '../../../../utils';
import { BrowserWrapper } from '../../../../../../shared/tags/AddTagsTermsModal';
import DomainNavigator from '../../../../../../domain/nestedDomains/domainNavigator/DomainNavigator';
import ClickOutside from '../../../../../../shared/ClickOutside';
import { TagTermLabel } from '../../../../../../shared/tags/TagTermLabel';
import { useTranslation } from '../../../../../../../i18n/useTranslation';

type Props = {
    urns: string[];
    onCloseModal: () => void;
    refetch?: () => Promise<any>;
    defaultValue?: { urn: string; entity?: Entity | null };
    onOkOverride?: (result: string) => void;
    titleOverride?: string;
};

type SelectedDomain = {
    displayName: string;
    type: EntityType;
    urn: string;
    entity?: Entity | null;
};

const CustomChip = styled(CustomTag)`
    > div {
        &:before {
            display: none;
        }
    }
`;

export const SetDomainModal = ({ urns, onCloseModal, refetch, defaultValue, onOkOverride, titleOverride }: Props) => {
    const { t } = useTranslation();
    const entityRegistry = useEntityRegistry();
    const [isFocusedOnInput, setIsFocusedOnInput] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [selectedDomain, setSelectedDomain] = useState<SelectedDomain | undefined>(
        defaultValue
            ? {
                  displayName: entityRegistry.getDisplayName(EntityType.Domain, defaultValue?.entity),
                  type: EntityType.Domain,
                  urn: defaultValue?.urn,
                  entity: defaultValue?.entity,
              }
            : undefined,
    );
    const [domainSearch, { data: domainSearchData }] = useGetSearchResultsLazyQuery();
    const domainSearchResults =
        domainSearchData?.search?.searchResults?.map((searchResult) => searchResult.entity) || [];
    const [batchSetDomainMutation] = useBatchSetDomainMutation();
    const inputEl = useRef(null);
    const isShowingDomainNavigator = !inputValue && isFocusedOnInput;

    const onModalClose = () => {
        setInputValue('');
        setSelectedDomain(undefined);
        onCloseModal();
    };

    const handleSearch = (text: string) => {
        domainSearch({
            variables: {
                input: {
                    type: EntityType.Domain,
                    query: text,
                    start: 0,
                    count: 5,
                },
            },
        });
    };

    // Renders a search result in the select dropdown.
    const renderSearchResult = (entity: Entity) => {
        const displayName = entityRegistry.getDisplayName(entity.type, entity);
        return (
            <Select.Option value={entity.urn} key={entity.urn}>
                <DomainLabel name={displayName} />
            </Select.Option>
        );
    };

    const domainResult = !inputValue || inputValue.length === 0 ? [] : domainSearchResults;

    const domainSearchOptions = domainResult?.map((result) => {
        return renderSearchResult(result);
    });

    const onSelectDomain = (newUrn: string) => {
        if (inputEl && inputEl.current) {
            (inputEl.current as any).blur();
        }
        const filteredDomains = domainResult?.filter((entity) => entity.urn === newUrn).map((entity) => entity) || [];
        if (filteredDomains.length) {
            const domain = filteredDomains[0];
            setSelectedDomain({
                displayName: entityRegistry.getDisplayName(EntityType.Domain, domain),
                type: EntityType.Domain,
                urn: newUrn,
                entity: domain,
            });
        }
        setInputValue('');
    };

    function selectDomainFromBrowser(domain: Domain) {
        setIsFocusedOnInput(false);
        setSelectedDomain({
            displayName: entityRegistry.getDisplayName(EntityType.Domain, domain),
            type: EntityType.Domain,
            urn: domain.urn,
            entity: domain,
        });
    }

    const chipRender = ({ selectedItem, onClose }) => {
        const onPreventMouseDown = (event) => {
            event.preventDefault();
            event.stopPropagation();
        };

        return (
            <CustomChip onMouseDown={onPreventMouseDown} closable onClose={onClose} className="base-tag">
                <TagTermLabel entity={selectedItem} />
            </CustomChip>
        );
    };

    const onDeselectDomain = () => {
        setInputValue('');
        setSelectedDomain(undefined);
    };

    const onOk = () => {
        if (!selectedDomain) {
            return;
        }

        if (onOkOverride) {
            onOkOverride(selectedDomain?.urn);
            return;
        }

        batchSetDomainMutation({
            variables: {
                input: {
                    resources: [...urns.map((urn) => ({ resourceUrn: urn }))],
                    domainUrn: selectedDomain.urn,
                },
            },
        })
            .then(({ errors }) => {
                if (!errors) {
                    message.success({ content: t('Updated Domain!'), duration: 2 });
                    refetch?.();
                    onModalClose();
                    setSelectedDomain(undefined);
                }
            })
            .catch((e) => {
                message.destroy();
                message.error(
                    handleBatchError(urns, e, {
                        content: t('Failed to add assets to Domain: {{message}}', { message: e.message || '' }),
                        duration: 3,
                    }),
                );
            });
    };

    // Handle the Enter press
    useEnterKeyListener({
        querySelectorToExecuteClick: '#setDomainButton',
    });

    function handleBlur() {
        setInputValue('');
    }

    function handleCLickOutside() {
        // delay closing the domain navigator so we don't get a UI "flash" between showing search results and navigator
        setTimeout(() => setIsFocusedOnInput(false), 0);
    }

    return (
        <Modal
            visible
            centered
            className="f-modal"
            onCancel={onModalClose}
            title={<h1>{titleOverride || t('Set Domain')}</h1>}
            footer={
                <>
                    <Button onClick={onModalClose} type="default">
                        {t('Cancel')}
                    </Button>
                    <Button
                        id="setDomainButton"
                        disabled={selectedDomain === undefined || selectedDomain?.urn === defaultValue?.urn}
                        onClick={onOk}
                        type="primary"
                    >
                        {t('Save')}
                    </Button>
                </>
            }
        >
            <Form component={false}>
                <Form.Item className="mb-0">
                    <ClickOutside onClickOutside={handleCLickOutside}>
                        <Select
                            autoFocus
                            showArrow
                            autoClearSearchValue
                            showSearch
                            size="large"
                            searchValue={inputValue}
                            value={[]}
                            mode="multiple"
                            filterOption={false}
                            aria-label={t('Select Domain')}
                            defaultActiveFirstOption={false}
                            placeholder={
                                <>
                                    <SearchOutlined className="f-color-dark-black-s100" /> {t('Search for Domains...')}
                                </>
                            }
                            onSelect={(domainUrn: string) => onSelectDomain(domainUrn)}
                            onDeselect={onDeselectDomain}
                            onSearch={(value: string) => {
                                handleSearch(value);
                                setInputValue(value);
                            }}
                            ref={inputEl}
                            onBlur={handleBlur}
                            onFocus={() => setIsFocusedOnInput(true)}
                            dropdownStyle={isShowingDomainNavigator ? { display: 'none' } : {}}
                        >
                            {domainSearchOptions}
                        </Select>
                        <BrowserWrapper isHidden={!isShowingDomainNavigator} width="100%">
                            <DomainNavigator selectDomainOverride={selectDomainFromBrowser} showIcon />
                        </BrowserWrapper>
                        <div className="f-tag-container mt-2">
                            {selectedDomain &&
                                chipRender({
                                    selectedItem: selectedDomain?.entity,
                                    onClose: () => {
                                        onDeselectDomain();
                                    },
                                })}
                        </div>
                    </ClickOutside>
                </Form.Item>
            </Form>
        </Modal>
    );
};
